import React, { useEffect, useState, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { useLessonStore } from '@/store/lessonStore'
import { apiClient } from '@/services/api'
import { Users, Clock, Activity, MessageSquare, Trophy, CheckCircle, FileText, Projector, Youtube, Pause } from 'lucide-react'
import type { Lesson, LessonActivity } from '@/types'
import { BlocklyActivity } from '@/components/blockly/BlocklyActivity'
import { PotatoPiratesActivity } from '@/components/potatoPirates/PotatoPiratesActivity'
import { ShellActivity } from '@/components/player/ShellActivity'
import { PhishingQuizActivity } from '@/components/player/PhishingQuizActivity'
import { PacketAnalysisActivity } from '@/components/player/PacketAnalysisActivity'
import { CyberHygieneQuizActivity } from '@/components/player/CyberHygieneQuizActivity'
import { CodingQuizActivity } from '@/components/player/CodingQuizActivity'
import { PythonReplActivity } from '@/components/player/PythonReplActivity'
import { LessonResults } from '@/components/player/LessonResults'

function PdfSlideViewer({ activityId, page, lessonId }: {
  activityId: string
  page: number
  lessonId: string
}) {
  const slideUrl = `/api/v1/lessons/${lessonId}/activities/${activityId}/slides?page=${page}`

  return (
    <div className="flex flex-col items-center gap-3">
      <img
        key={page}
        src={slideUrl}
        alt={`Slide ${page}`}
        onContextMenu={(e) => e.preventDefault()}
        className="w-full object-contain rounded-lg select-none pointer-events-none max-w-full"
      />
      <p className="text-xs text-theme-text-muted">Slide {page}</p>
    </div>
  )
}

function McqActivity({ activity, runCode, sessionId, onResponded }: {
  activity: LessonActivity
  runCode: string
  sessionId: string
  onResponded: () => void
}) {
  const [selected, setSelected] = useState<number | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const options: string[] = activity.activity_data?.options || []

  const handleSubmit = async () => {
    if (selected === null) return
    setSubmitting(true)
    try {
      await apiClient.submitResponsePublic(runCode, sessionId, activity.id, { answer: selected })
      onResponded()
    } catch {
      // submission failed
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="space-y-3">
      <p className="text-lg font-medium mb-4">{activity.activity_data?.question}</p>
      {activity.activity_data?.question_image_url && (
        <img src={activity.activity_data.question_image_url} alt="" className="max-w-full max-h-64 rounded-lg mx-auto mb-4 object-contain" />
      )}
      {options.map((option, idx) => (
        <button
          key={idx}
          onClick={() => setSelected(idx)}
          className={`w-full text-left px-4 py-3 rounded-lg border transition-colors ${
            selected === idx
              ? 'border-[var(--color-primary)] bg-[var(--color-primary-subtle)] text-[var(--color-text)]'
              : 'border-theme-border bg-surface-deep text-theme-text hover:border-theme-text-muted'
          }`}
        >
          <span className="font-bold mr-3">{String.fromCharCode(65 + idx)}.</span>
          {option}
        </button>
      ))}
      <button
        onClick={handleSubmit}
        disabled={selected === null || submitting}
        className="w-full mt-4 px-4 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold bg-theme-primary text-theme-on-primary"
      >
        {submitting ? 'Submitting...' : 'Submit Answer'}
      </button>
    </div>
  )
}

const SLIDE_TYPES = ['pdf_slideshow', 'google_slides', 'youtube_video']
const COMPACT_PADDING_TYPES = ['command_shell', 'phishing_quiz', 'packet_analysis', 'cyber_hygiene_quiz', 'coding', 'coding_quiz', 'python_repl', 'potato_pirates']
const SELF_MANAGED_HEADER_TYPES = ['mcq', ...COMPACT_PADDING_TYPES]

const getActivityType = (activity: LessonActivity) =>
  activity.activity_type || (activity as any).type

export function PlayerInterface() {
  const { runCode } = useParams()
  const navigate = useNavigate()
  const {
    currentLesson,
    currentActivity,
    currentSlide,
    players,
    isConnected,
    connectionError,
    isLoading,
    setCurrentLesson,
    connectToLesson,
    disconnectFromLesson,
    setLoading,
    setError
  } = useLessonStore()

  const [playerName, setPlayerName] = useState('')
  const [isJoined, setIsJoined] = useState(false)
  const [sessionId, setSessionId] = useState<string>('')
  const [playerData, setPlayerData] = useState<any>(null)
  const [currentScore, setCurrentScore] = useState(0)
  const [isRestoring, setIsRestoring] = useState(true)
  const [hasResponded, setHasResponded] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<number | null>(null)
  const [timedOut, setTimedOut] = useState(false)
  const [showPdf, setShowPdf] = useState(true)
  const timerRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    if (!runCode) {
      navigate('/join')
      return
    }

    // Try to restore session first
    restoreSession()

    return () => {
      disconnectFromLesson()
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    }
  }, [runCode])

  const loadLesson = async () => {
    try {
      setLoading(true)
      const response = await apiClient.getRunInfo(runCode!)

      if (!response.success) {
        throw new Error(response.message || 'Failed to load lesson')
      }

      const lesson: Lesson = response.data.lesson
      setCurrentLesson(lesson)

      // For player interface, we don't connect to authenticated channels yet
      // We'll connect after joining the lesson
    } catch (error: any) {
      setError(error.message || 'Failed to load lesson')
      if (error.response?.status === 404) {
        navigate('/join')
      }
    } finally {
      setLoading(false)
    }
  }

  const restoreSession = async () => {
    try {
      setIsRestoring(true)
      
      // Check for stored session
      const storedSessionId = localStorage.getItem(`session_${runCode}`)
      if (!storedSessionId) {
        // No session to restore
        await loadLesson()
        setIsRestoring(false)
        return
      }

      // Try to restore player state
      const response = await apiClient.getRunStatePublic(runCode!, storedSessionId)
      
      if (response.success && response.data.player) {
        // Session is valid, restore state
        const { lesson, player, current_activity } = response.data
        if (response.data.show_pdf !== undefined) setShowPdf(response.data.show_pdf)

        setCurrentLesson(lesson)
        setPlayerData(player)
        setPlayerName(player.display_name)
        setCurrentScore(player.total_score || 0)
        setSessionId(storedSessionId)
        setIsJoined(true)
        
        // Update current activity if exists
        if (current_activity) {
          useLessonStore.getState().setCurrentActivity(current_activity)
          setHasResponded(current_activity.player_has_responded || false)

          // Start timer if activity is active
          if (current_activity.status === 'active' && current_activity.started_at) {
            startTimer(current_activity)
          }
        }
        
        // Connect to WebSocket with restored session
        connectToLesson(
          lesson.id,
          'player',
          {
            runCode: runCode!,
            sessionId: storedSessionId
          }
        )
      } else {
        // Session invalid, clear it and load lesson normally
        localStorage.removeItem(`session_${runCode}`)
        await loadLesson()
      }
    } catch {
      // Clear invalid session and load lesson
      localStorage.removeItem(`session_${runCode}`)
      await loadLesson()
    } finally {
      setIsRestoring(false)
    }
  }

  // Timer management
  const startTimer = (activity: LessonActivity) => {
    if (SLIDE_TYPES.includes(activity.activity_type)) return

    // Clear any existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current)
    }

    // Calculate initial time remaining
    let initial: number | null = null
    if (activity.started_at && activity.time_limit_seconds) {
      const elapsed = Math.floor((Date.now() - new Date(activity.started_at).getTime()) / 1000)
      initial = Math.max(0, activity.time_limit_seconds - elapsed)
    } else if (activity.time_remaining != null) {
      initial = activity.time_remaining
    }

    if (initial !== null) {
      setTimeRemaining(initial)
      timerRef.current = setInterval(() => {
        setTimeRemaining(prev => {
          if (prev === null || prev <= 0) {
            if (timerRef.current) clearInterval(timerRef.current)
            setTimedOut(true)
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }
  }

  // Listen for activity updates
  useEffect(() => {
    if (currentActivity?.status === 'active' && currentActivity.started_at) {
      setTimedOut(false)
      startTimer(currentActivity)
    } else if (currentActivity?.status === 'paused') {
      // Paused — stop the countdown but keep the frozen time visible
      if (timerRef.current) {
        clearInterval(timerRef.current)
      }
    } else {
      // Clear timer if activity is not active
      if (timerRef.current) {
        clearInterval(timerRef.current)
        setTimeRemaining(null)
      }
    }

    // Reset response state when activity changes
    if (currentActivity && playerData) {
      setHasResponded(currentActivity.player_has_responded || false)
    }
  }, [currentActivity, playerData])

  // Update score when player data changes
  useEffect(() => {
    if (playerData?.total_score !== undefined) {
      setCurrentScore(playerData.total_score)
    }
  }, [playerData])

  // Listen for player updates from WebSocket
  useEffect(() => {
    if (!sessionId || !isJoined) return

    // Find current player in the players list
    const currentPlayer = players.find(p => p.session_id === sessionId || p.id === playerData?.id)
    if (currentPlayer) {
      setPlayerData(currentPlayer)
      setCurrentScore(currentPlayer.total_score || 0)
    }
  }, [players, sessionId, playerData?.id, isJoined])

  const handleJoinLesson = async () => {
    if (!runCode || !playerName.trim()) return

    try {
      setLoading(true)

      // Generate or get existing session ID
      let currentSessionId = sessionId || localStorage.getItem(`session_${runCode}`)
      if (!currentSessionId) {
        currentSessionId = crypto.randomUUID()
        setSessionId(currentSessionId)
        localStorage.setItem(`session_${runCode}`, currentSessionId)
      }

      const response = await apiClient.joinRunPublic(
        runCode!,
        playerName.trim(),
        currentSessionId
      )

      if (response.success) {
        setIsJoined(true)
        setSessionId(response.data.session_id)
        setPlayerData(response.data.player)
        setCurrentScore(response.data.player.total_score || 0)
        localStorage.setItem(`session_${runCode}`, response.data.session_id)

        // Now connect to real-time updates as guest player
        connectToLesson(
          response.data.lesson.id,
          'player',
          {
            runCode: runCode!,
            sessionId: response.data.session_id
          }
        )
      }
    } catch (error: any) {
      setError(error.response?.data?.error || error.message || 'Failed to join lesson')
    } finally {
      setLoading(false)
    }
  }

  if (!currentLesson || isRestoring) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin w-8 h-8 border-2 border-[var(--color-primary)] border-t-transparent rounded-full mx-auto mb-4"></div>
          <p>{isRestoring ? 'Restoring session...' : 'Loading lesson...'}</p>
        </div>
      </div>
    )
  }

  if (!isJoined) {
    return (
      <div className="min-h-screen bg-theme-bg text-theme-text flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-surface-deep rounded-lg p-8 max-w-md w-full mx-4"
        >
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold mb-2">{currentLesson.name}</h1>
            <p className="text-theme-text-muted">Code: {currentLesson.run_code}</p>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Your Name</label>
              <input
                type="text"
                value={playerName}
                onChange={(e) => setPlayerName(e.target.value)}
                placeholder="Enter your name"
                className="w-full px-4 py-3 bg-surface-hover border border-theme-border rounded-lg text-theme-text placeholder-theme-text-muted focus:border-[var(--color-primary)] focus:outline-none"
                onKeyPress={(e) => e.key === 'Enter' && handleJoinLesson()}
              />
            </div>

            <button
              onClick={handleJoinLesson}
              disabled={!playerName.trim() || isLoading}
              className="w-full px-4 py-3 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-semibold bg-theme-primary text-theme-on-primary"
            >
              {isLoading ? 'Joining...' : 'Join Lesson'}
            </button>
          </div>

          <div className="mt-6 flex items-center justify-center gap-4 text-sm text-theme-text-muted">
            <div className="flex items-center gap-1">
              <Users className="w-4 h-4" />
              <span>{players.length} players</span>
            </div>
            <div className={`flex items-center gap-1 ${
              currentLesson.status === 'ready' ? 'text-blue-400' :
              currentLesson.status === 'running' ? 'text-green-400' :
              'text-theme-text-muted'
            }`}>
              <Activity className="w-4 h-4" />
              <span>{currentLesson.status}</span>
            </div>
          </div>
        </motion.div>
      </div>
    )
  }

  // End-of-lesson recap: leaderboard, this player's rank, and per-activity recap
  if (currentLesson.status === 'completed') {
    return (
      <LessonResults
        fetchResults={() => apiClient.getLessonResultsPublic(runCode!, sessionId)}
        playerId={playerData?.id}
        onExit={() => navigate('/join')}
      />
    )
  }

  return (
    <div className="min-h-screen bg-theme-bg text-theme-text">
      {/* Header */}
      <div className="border-b border-theme-border bg-surface">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold">{currentLesson.name}</h1>
              <div className="flex items-center gap-4 mt-1 text-sm text-theme-text-muted">
                <span>Code: {currentLesson.run_code}</span>
                <div className={`flex items-center gap-1 ${
                  isConnected ? 'text-green-400' : 'text-red-400'
                }`}>
                  <div className={`w-2 h-2 rounded-full ${
                    isConnected ? 'bg-green-400' : 'bg-red-400'
                  }`}></div>
                  {isConnected ? 'Connected' : 'Disconnected'}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-6">
              <div className="text-right">
                <div className="text-sm text-theme-text-muted">Score</div>
                <div className="text-xl font-bold flex items-center gap-1">
                  <Trophy className="w-5 h-5 text-yellow-500" />
                  {currentScore}
                </div>
              </div>
              <div className="text-right">
                <div className="text-sm text-theme-text-muted">Players</div>
                <div className="text-xl font-bold">{players.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="container mx-auto px-4 py-6">
        {currentActivity && !timedOut ? (
          <div className="max-w-2xl mx-auto">
            {/* Activity display */}
            <motion.div
              key={currentActivity.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className={`bg-surface-deep rounded-lg mb-6 ${
                COMPACT_PADDING_TYPES.includes(getActivityType(currentActivity))
                  ? 'p-3'
                  : 'p-6'
              }`}
            >
              {!SELF_MANAGED_HEADER_TYPES.includes(getActivityType(currentActivity)) && (
              <div className="text-center mb-6">
                <h2 className="text-2xl font-bold mb-2">{currentActivity.title}</h2>
                {currentActivity.instruction && (
                  <p className="text-theme-text-muted">{currentActivity.instruction}</p>
                )}
              </div>
              )}

              {/* Timer */}
              {timeRemaining !== null && (currentActivity?.status === 'active' || currentActivity?.status === 'paused') &&
               !COMPACT_PADDING_TYPES.includes(getActivityType(currentActivity)) && (
                <div className="text-center mb-6">
                  <div className={`text-3xl font-mono font-bold ${
                    currentActivity?.status === 'paused' ? 'text-yellow-400' :
                    timeRemaining <= 10 ? 'text-red-400' : 'text-blue-400'
                  }`}>
                    {Math.floor(timeRemaining / 60)}:
                    {(timeRemaining % 60).toString().padStart(2, '0')}
                  </div>
                  <div className="text-sm text-theme-text-muted">
                    {currentActivity?.status === 'paused' ? 'Paused' : 'Time Remaining'}
                  </div>
                </div>
              )}

              {/* Activity content */}
              <div className={`bg-surface-hover rounded-lg ${
                COMPACT_PADDING_TYPES.includes(getActivityType(currentActivity))
                  ? 'overflow-hidden p-0'
                  : 'p-6'
              }`}>
                {currentActivity.activity_type === 'google_slides' ? (
                  <div className="text-center text-theme-text-muted py-8">
                    <Projector className="w-16 h-16 mx-auto mb-3 opacity-50" />
                    <p className="text-lg font-medium mb-1">Look at the captain's screen</p>
                    <p className="text-sm text-theme-muted">The slides are being presented by your captain</p>
                  </div>
                ) : currentActivity.activity_type === 'youtube_video' ? (
                  <div className="text-center text-theme-text-muted py-8">
                    <Youtube className="w-16 h-16 mx-auto mb-3 opacity-50" />
                    <p className="text-lg font-medium mb-1">Look at the captain's screen</p>
                    <p className="text-sm text-theme-muted">A video is being presented by your captain</p>
                  </div>
                ) : currentActivity.activity_type === 'pdf_slideshow' ? (
                  !showPdf ? (
                    <div className="text-center text-theme-text-muted py-8">
                      <FileText className="w-16 h-16 mx-auto mb-3 opacity-50" />
                      <p className="text-lg font-medium mb-1">Look at the captain's screen</p>
                      <p className="text-sm text-theme-muted">The slides are being presented by your captain</p>
                    </div>
                  ) : currentLesson?.id ? (
                    <PdfSlideViewer
                      activityId={currentActivity.id}
                      page={currentSlide}
                      lessonId={currentLesson.id}
                    />
                  ) : (
                    <div className="text-center text-theme-text-muted">
                      <FileText className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>Waiting for slide…</p>
                    </div>
                  )
                ) : (getActivityType(currentActivity) === 'coding') ? (
                  <div className="p-4">
                    <BlocklyActivity
                      activity={currentActivity}
                      disabled={hasResponded}
                      onSubmit={async (responseData) => {
                        try {
                          await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, responseData)
                          setHasResponded(true)
                        } catch {
                          // submission failed
                        }
                      }}
                    />
                  </div>
                ) : (getActivityType(currentActivity) === 'potato_pirates') ? (
                  <div className="p-4">
                    <PotatoPiratesActivity
                      activity={currentActivity}
                      disabled={hasResponded}
                      onSubmit={async (responseData) => {
                        try {
                          await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, responseData)
                          setHasResponded(true)
                        } catch {
                          // submission failed
                        }
                      }}
                    />
                  </div>
                ) : hasResponded ? (
                  <div className="text-center">
                    <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-400" />
                    <h3 className="text-xl font-semibold mb-2">Response Submitted!</h3>
                    <p className="text-theme-text-muted">
                      Waiting for other players to submit their responses...
                    </p>
                  </div>
                ) : (getActivityType(currentActivity) === 'mcq') ? (
                  <McqActivity
                    activity={currentActivity}
                    runCode={runCode!}
                    sessionId={sessionId}
                    onResponded={() => setHasResponded(true)}
                  />
                ) : (getActivityType(currentActivity) === 'command_shell') ? (
                  <ShellActivity
                    activityData={currentActivity.activity_data as Record<string, any>}
                    disabled={hasResponded}
                    onExecuteCommand={async (cmd) => {
                      const res = await apiClient.executeShellCommand(runCode!, sessionId, currentActivity.id, cmd)
                      return { success: res.success, data: res.data, error: (res as any).error }
                    }}
                    onSubmitAnswer={async (answer) => {
                      await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, { answer })
                      setHasResponded(true)
                      return { success: true }
                    }}
                  />
                ) : (getActivityType(currentActivity) === 'phishing_quiz') ? (
                  <PhishingQuizActivity
                    activityData={currentActivity.activity_data as Record<string, any>}
                    disabled={hasResponded}
                    onSubmitQuiz={async (data) => {
                      await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, data)
                      setHasResponded(true)
                      return { success: true }
                    }}
                  />
                ) : (getActivityType(currentActivity) === 'packet_analysis') ? (
                  <PacketAnalysisActivity
                    activityData={currentActivity.activity_data as Record<string, any>}
                    disabled={hasResponded}
                    onSubmitQuiz={async (data) => {
                      await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, data)
                      setHasResponded(true)
                      return { success: true }
                    }}
                  />
                ) : (getActivityType(currentActivity) === 'cyber_hygiene_quiz') ? (
                  <CyberHygieneQuizActivity
                    activityData={currentActivity.activity_data as Record<string, any>}
                    disabled={hasResponded}
                    onSubmitQuiz={async (data) => {
                      await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, data)
                      setHasResponded(true)
                      return { success: true }
                    }}
                  />
                ) : (getActivityType(currentActivity) === 'coding_quiz') ? (
                  <CodingQuizActivity
                    activityData={currentActivity.activity_data as Record<string, any>}
                    disabled={hasResponded}
                    onSubmitQuiz={async (data) => {
                      await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, data)
                      setHasResponded(true)
                      return { success: true }
                    }}
                  />
                ) : (getActivityType(currentActivity) === 'python_repl') ? (
                  <PythonReplActivity
                    activityData={currentActivity.activity_data as Record<string, any>}
                    disabled={hasResponded}
                    onSubmitResponse={async (data) => {
                      await apiClient.submitResponsePublic(runCode!, sessionId, currentActivity.id, data)
                      setHasResponded(true)
                      return { success: true }
                    }}
                  />
                ) : (
                  <div className="text-center text-theme-text-muted">
                    <MessageSquare className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Activity type <strong>{currentActivity.activity_type}</strong> is not yet supported on the player screen.</p>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        ) : (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 mx-auto mb-4 text-theme-muted" />
            <h2 className="text-xl font-semibold mb-2">Waiting for activity</h2>
            <p className="text-theme-text-muted">
              The captain will start the next activity soon
            </p>
          </div>
        )}
      </div>

      {/* Connection error */}
      {connectionError && (
        <div className="fixed bottom-4 right-4 bg-red-900 text-red-100 p-4 rounded-lg max-w-sm">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-400 rounded-full"></div>
            <span className="font-medium">Connection Error</span>
          </div>
          <p className="text-sm mt-1">{connectionError}</p>
        </div>
      )}

      {/* Paused overlay */}
      {currentActivity?.status === 'paused' && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 backdrop-blur-sm">
          <div className="text-center text-white">
            <Pause className="w-16 h-16 text-yellow-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-yellow-400 mb-2">Activity Paused</h2>
            <p className="text-theme-text-muted">Your captain has paused the activity. Please wait.</p>
          </div>
        </div>
      )}
    </div>
  )
}